'use client';

import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Cog6ToothIcon, ArrowUturnLeftIcon, ArrowUturnRightIcon, ArrowPathIcon, SparklesIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { MessageBubble, ChatInputArea, ChatInputAreaHandle } from '@contentgrowth/llm-service/ui/react';
import { ChatMessage } from '@contentgrowth/llm-service/ui/react';
// History management is now handled by ArticleEditor and passed as props
import DiffViewer from './DiffViewer';
import Tooltip from '@/components/common/Tooltip';
import { useAIShortcuts } from '@/hooks/useAIShortcuts';
import AIShortcutsDialog from '@/components/markdown/components/AIShortcutsDialog';

interface AgentChatPanelProps {
  articleId: string;
  // Chat state passed from parent
  chatState: {
    messages: ChatMessage[];
    setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    input: string;
    handleInputChange: (e: any) => void;
    handleSubmit: (e: any) => void;
    isLoading: boolean;
    error: any;
    sendMessage: (content: string) => Promise<boolean>;
    stop?: () => void;
  };
  // Handlers for system actions
  onUndoClick: () => void;
  onRedoClick: () => void;
  onResetClick: () => void;

  // History management props from ArticleEditor
  canUndo?: boolean;
  canRedo?: boolean;
  lastChangeSummary?: string;
  nextChangeSummary?: string;
  disabled?: boolean;
  disabledReason?: string;
  userAvatarUrl?: string;
  inputMode?: 'none' | 'text';
  disableAutoFocus?: boolean;
  hideVoiceButton?: boolean;
}

export interface AgentChatPanelRef {
  focus: () => void;
  replaceSelection: (text: string) => void;
}

const AgentChatPanel = forwardRef<AgentChatPanelRef, AgentChatPanelProps>(({
  articleId,
  chatState,
  onUndoClick,
  onRedoClick,
  onResetClick,
  canUndo,
  canRedo,
  lastChangeSummary,
  nextChangeSummary,
  disabled = false,
  disabledReason,
  userAvatarUrl,
  inputMode,
  disableAutoFocus = false,
  hideVoiceButton = false,
}, ref) => {
  const [showDiffViewer, setShowDiffViewer] = useState(false);
  const [showShortcutsDialog, setShowShortcutsDialog] = useState(false);
  const [selectedDiff, setSelectedDiff] = useState<{ oldContent: string; newContent: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<ChatInputAreaHandle>(null); // Ref for the ChatInput component

  const { messages, input, handleInputChange, handleSubmit, isLoading, error, sendMessage, stop } = chatState;

  const { shortcuts, addShortcut, updateShortcut, deleteShortcut, reorderShortcuts, resetToDefaults } = useAIShortcuts();

  // Expose sendMessage function via ref (compatibility/unused)
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
    replaceSelection: (text: string) => {
      inputRef.current?.replaceSelection(text);
    }
  }), []);

  // Get tooltip text for undo/redo buttons
  const getUndoTooltip = () => {
    if (canUndo) {
      const summary = lastChangeSummary || 'edit';
      return `Undo: ${summary}`;
    }
    return 'Nothing to undo';
  };

  const getRedoTooltip = () => {
    if (canRedo) {
      const summary = nextChangeSummary || 'edit';
      return `Redo: ${summary}`;
    }
    return 'Nothing to redo';
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    
    // Auto-focus the input only if:
    // 1. We are not currently loading an AI response
    // 2. Auto-focus is not explicitly disabled (e.g., hidden panel, open dialogs)
    // 3. The last message is NOT a system message (prevents focus steal on "Manual edit detected")
    const lastMessage = messages[messages.length - 1];
    const isSystemMessage = lastMessage?.role === 'system';

    if (!isLoading && !disableAutoFocus && !isSystemMessage) {
      inputRef.current?.focus();
    }
  }, [messages, isLoading, disableAutoFocus]);

  const handleQuickAction = async (prompt: string) => {
    if (disabled || isLoading) return;
    await sendMessage(prompt);
  };

  return (
    <>
      <DiffViewer
        isOpen={showDiffViewer}
        onClose={() => setShowDiffViewer(false)}
        oldContent={selectedDiff?.oldContent || ''}
        newContent={selectedDiff?.newContent || ''}
      />

      <AIShortcutsDialog
        isOpen={showShortcutsDialog}
        onClose={() => setShowShortcutsDialog(false)}
        shortcuts={shortcuts}
        addShortcut={addShortcut}
        updateShortcut={updateShortcut}
        deleteShortcut={deleteShortcut}
        reorderShortcuts={reorderShortcuts}
        resetToDefaults={resetToDefaults}
      />

      <div className="flex flex-col h-full min-h-0 bg-white text-gray-800 rounded-lg shadow-lg border border-gray-200">
        <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-gray-50/50 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <SparklesIcon className="w-5 h-5 text-blue-500" />
            <h2 className="font-bold text-lg text-gray-800">Content Copilot</h2>
          </div>
          <div className="flex items-center space-x-1">
            <Tooltip content="Configure Shortcuts" position="top">
              <button
                onClick={() => setShowShortcutsDialog(true)}
                className="p-2 text-gray-600 hover:text-gray-800"
              >
                <Cog6ToothIcon className="h-4 w-4" />
              </button>
            </Tooltip>

            <div className="w-px h-4 bg-gray-300 mx-1" />

            <Tooltip
              content={getUndoTooltip()}
              position="top"
            >
              <button
                onClick={onUndoClick}
                disabled={!canUndo}
                className="p-2 text-gray-600 hover:text-gray-800 disabled:text-gray-300 disabled:cursor-not-allowed"
              >
                <ArrowUturnLeftIcon className="h-4 w-4" />
              </button>
            </Tooltip>

            <Tooltip
              content={getRedoTooltip()}
              position="top"
            >
              <button
                onClick={onRedoClick}
                disabled={!canRedo}
                className="p-2 text-gray-600 hover:text-gray-800 disabled:text-gray-300 disabled:cursor-not-allowed"
              >
                <ArrowUturnRightIcon className="h-4 w-4" />
              </button>
            </Tooltip>

            <Tooltip
              content="Reset History"
              position="top"
            >
              <button
                onClick={onResetClick}
                className="p-2 text-gray-600 hover:text-gray-800"
              >
                <ArrowPathIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
        </div>
        <div className="flex-grow min-h-0 p-4 overflow-y-auto space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 pt-8">
              <p className="text-lg">Start by giving your content copilot an instruction.</p>
              <p className="text-sm">e.g., "Fix spelling and grammar" or "Make this sound more professional."</p>
            </div>
          ) : (
            messages.map((msg, index) => (
              <MessageBubble
                key={index}
                message={msg}
                isUser={msg.role === 'user'}
                userAvatarUrl={userAvatarUrl}
                onViewChanges={(diff) => {
                  setSelectedDiff(diff);
                  setShowDiffViewer(true);
                }}
              />
            ))
          )}
          {isLoading && !disabled && (
            <div className="flex justify-start">
              <div className="p-3 rounded-lg bg-gray-100 border border-gray-200 shadow">
                <span className="italic text-gray-500">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="p-4 border-t border-gray-200 bg-gray-50/50 rounded-b-lg">
          {error && (
            <div className="mb-2 flex items-center text-sm text-red-600">
              <ExclamationCircleIcon className="h-5 w-5 mr-2" />
              <span>{error.message}</span>
            </div>
          )}
          {disabled && disabledReason && (
            <div className="mb-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start text-sm text-yellow-800">
              <ExclamationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{disabledReason}</span>
            </div>
          )}

          {/* Quick Actions Bar */}
          {!disabled && !isLoading && shortcuts.length > 0 && (
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1 scrollbar-hide">
              {shortcuts.slice(0, 5).map(shortcut => (
                <Tooltip
                  key={shortcut.id}
                  content={shortcut.prompt}
                  position="top"
                  delay={300}
                  inline={true}
                >
                  <button
                    onClick={() => handleQuickAction(shortcut.prompt)}
                    className="whitespace-nowrap px-3 py-1 bg-white border border-blue-200 text-blue-700 text-xs rounded-full hover:bg-blue-50 transition-colors shadow-sm"
                  >
                    {shortcut.name}
                  </button>
                </Tooltip>
              ))}
            </div>
          )}

          <ChatInputArea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            isSending={isLoading}
            showInputForm={!disabled}
            onStop={stop}
            onSubmit={(msg) => sendMessage(msg)}
            currentTask={null}
            hintText="Press Shift + Enter for a new line"
            inputMode={inputMode}
            hideVoiceButton={hideVoiceButton}
          />
        </div>
      </div>
    </>
  );
});

AgentChatPanel.displayName = 'AgentChatPanel';

export default AgentChatPanel;
